import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullseye, faChartLine, faSearch, faUserCheck, faUsers } from '@fortawesome/free-solid-svg-icons';
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { getFirestore } from '@firebase/firestore';
import { collection, getDocs } from '@firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { app } from '../firebase.js';
import defaulProfPic from '../Images/defprofile.png';
import MaskedPhone from '../Components/MaskedPhone';
import { logAdminActivity } from '../utils/activityLogger';
import {
  getAdmissionTimelineValue,
  getStudentDisplayName,
  isPaidAdmissionFeeButNotJoined,
  normalizeAdmissionStatus
} from '../utils/admissions';
import { toDateValue } from '../utils/financeWorkflow';

const WEEKLY_TARGET = 5;
const MONTHLY_TARGET = 20;
const JOINED_STATUSES = new Set([
  'student',
  'active',
  'completed',
  'paused',
  'deferred',
  'dropped',
  'expelled',
  'joined'
]);

const formatNumber = (value) => Number(value || 0).toLocaleString('en-KE');

const formatPercent = (value, digits = 1) => {
  if (!Number.isFinite(value)) {
    return '0%';
  }

  return `${value.toFixed(digits)}%`;
};

const getApplicationDate = (student = {}) => (
  toDateValue(student.appDate || student.createdAt || student.admissionDate || student.classStartDate)
);

const getAdmissionDate = (student = {}) => (
  toDateValue(student.admissionDate || student.admissionFeePaidAt || student.classStartDate || student.createdAt || student.appDate)
);

const isJoinedStudent = (student = {}) => {
  const status = normalizeAdmissionStatus(student.userStatus || student.Status || student.status || '');
  return JOINED_STATUSES.has(status);
};

const getWeekBounds = (dateValue = new Date()) => {
  const start = new Date(dateValue);
  const dayOffset = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - dayOffset);
  start.setHours(0, 0, 0, 0);

  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);

  return { start, end };
};

const getMonthBounds = (dateValue = new Date()) => ({
  start: new Date(dateValue.getFullYear(), dateValue.getMonth(), 1, 0, 0, 0, 0),
  end: new Date(dateValue.getFullYear(), dateValue.getMonth() + 1, 0, 23, 59, 59, 999)
});

const getYearBounds = (dateValue = new Date()) => ({
  start: new Date(dateValue.getFullYear(), 0, 1, 0, 0, 0, 0),
  end: new Date(dateValue.getFullYear(), 11, 31, 23, 59, 59, 999)
});

function ApplicationsAwaitingJoinComp() {
  const db = getFirestore(app);
  const navigate = useNavigate();

  const [studentsList, setStudentList] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countAllData, setcountAllData] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCourse, setFilterCourse] = useState('All');
  const [filterCountry, setFilterCountry] = useState('All');
  const lastSearchRef = useRef('');
  const lastCourseRef = useRef(filterCourse);
  const lastCountryRef = useRef(filterCountry);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const querySnapshot = await getDocs(collection(db, 'users'));
        const studentsItem = [];

        querySnapshot.forEach((doc) => {
          const student = { id: doc.id, ...doc.data() };
          studentsItem.push(student);
        });

        const awaitingJoinStudents = studentsItem.filter((student) => isPaidAdmissionFeeButNotJoined(student));

        awaitingJoinStudents.sort((firstStudent, secondStudent) => (
          getAdmissionTimelineValue(secondStudent) - getAdmissionTimelineValue(firstStudent)
        ));

        setAllStudents(studentsItem);
        setStudentList(awaitingJoinStudents);
        setcountAllData(awaitingJoinStudents.length);
      } catch (error) {
        console.error('Failed to load paid admission fee applicants:', error);
        setAllStudents([]);
        setStudentList([]);
        setcountAllData(0);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [db]);

  useEffect(() => {
    const trimmed = searchTerm.trim();
    const timeoutId = setTimeout(() => {
      if (trimmed === lastSearchRef.current) return;
      lastSearchRef.current = trimmed;
      if (trimmed) {
        logAdminActivity({
          action: 'Search',
          details: `Applications Awaiting Join search: "${trimmed}"`,
          meta: { page: 'Applications Awaiting Join', query: trimmed }
        });
      }
    }, 600);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    if (filterCourse === lastCourseRef.current) return;
    lastCourseRef.current = filterCourse;
    logAdminActivity({
      action: 'Filter',
      details: `Applications Awaiting Join course filter: ${filterCourse}`,
      meta: { page: 'Applications Awaiting Join', filter: 'course', value: filterCourse }
    });
  }, [filterCourse]);

  useEffect(() => {
    if (filterCountry === lastCountryRef.current) return;
    lastCountryRef.current = filterCountry;
    logAdminActivity({
      action: 'Filter',
      details: `Applications Awaiting Join country filter: ${filterCountry}`,
      meta: { page: 'Applications Awaiting Join', filter: 'country', value: filterCountry }
    });
  }, [filterCountry]);

  const viewStudent = (userId, studentName) => {
    logAdminActivity({
      action: 'View Student',
      details: `Opened student profile: ${studentName || 'student'}`,
      meta: { page: 'Applications Awaiting Join', studentId: userId, studentName: studentName || '' }
    });
    navigate('/view-student', { state: { myData: userId } });
  };

  const uniqueCourses = useMemo(
    () => Array.from(new Set(studentsList.map((student) => student.course).filter(Boolean))).sort(),
    [studentsList]
  );

  const uniqueCountries = useMemo(
    () => Array.from(new Set(studentsList.map((student) => student.nationality).filter(Boolean))).sort(),
    [studentsList]
  );

  const filteredStudents = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return studentsList.filter((student) => {
      const matchesSearch = !search || [
        getStudentDisplayName(student),
        student.course,
        student.nationality,
        student.phoneNumber,
        student.cohort,
        student.admissionFeePaymentReference
      ].some((value) => String(value || '').toLowerCase().includes(search));

      const matchesCourse = filterCourse === 'All' || student.course === filterCourse;
      const matchesCountry = filterCountry === 'All' || student.nationality === filterCountry;
      return matchesSearch && matchesCourse && matchesCountry;
    });
  }, [filterCourse, filterCountry, searchTerm, studentsList]);

  const formatDateTime = (value) => {
    if (!value) return '—';
    const dateObj = value.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(dateObj.getTime())) return '—';
    const datePart = dateObj.toLocaleDateString();
    const timePart = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return `${datePart} • ${timePart}`;
  };

  const totalCount = countAllData;
  const filteredCount = filteredStudents.length;
  const admissionsSnapshot = useMemo(() => {
    const now = new Date();
    const { start: weekStart, end: weekEnd } = getWeekBounds(now);
    const { start: monthStart, end: monthEnd } = getMonthBounds(now);
    const { start: yearStart, end: yearEnd } = getYearBounds(now);

    const applicantRows = allStudents
      .map((student) => ({
        ...student,
        applicationDate: getApplicationDate(student),
        admissionDate: getAdmissionDate(student)
      }))
      .filter((student) => student.applicationDate instanceof Date && !Number.isNaN(student.applicationDate.getTime()));

    const yearApplicants = applicantRows.filter((student) => student.applicationDate >= yearStart && student.applicationDate <= yearEnd);
    const yearAdmitted = yearApplicants.filter((student) => isJoinedStudent(student));
    const yearSuccessRate = yearApplicants.length > 0 ? (yearAdmitted.length / yearApplicants.length) * 100 : 0;
    const applicantsPerWeeklyTarget = yearSuccessRate > 0
      ? Math.ceil(WEEKLY_TARGET / (yearSuccessRate / 100))
      : null;
    const applicantsPerMonthlyTarget = yearSuccessRate > 0
      ? Math.ceil(MONTHLY_TARGET / (yearSuccessRate / 100))
      : null;

    const thisWeekAdmissions = applicantRows.filter((student) => (
      isJoinedStudent(student)
      && student.admissionDate >= weekStart
      && student.admissionDate <= weekEnd
    ));

    const thisMonthAdmissions = applicantRows.filter((student) => (
      isJoinedStudent(student)
      && student.admissionDate >= monthStart
      && student.admissionDate <= monthEnd
    ));

    return {
      thisWeekAdmissions: thisWeekAdmissions.length,
      thisMonthAdmissions: thisMonthAdmissions.length,
      yearSuccessRate,
      applicantsPerWeeklyTarget,
      applicantsPerMonthlyTarget,
      weeklyGap: Math.max(WEEKLY_TARGET - thisWeekAdmissions.length, 0),
      monthlyGap: Math.max(MONTHLY_TARGET - thisMonthAdmissions.length, 0)
    };
  }, [allStudents]);

  const admissionsGuidance = useMemo(() => {
    if (allStudents.length === 0) {
      return 'Once the admissions flow fills in, this snapshot will estimate how many applicants you need to stay on pace for the weekly and monthly targets.';
    }

    const weeklyApplicantsText = admissionsSnapshot.applicantsPerWeeklyTarget
      ? `about ${formatNumber(admissionsSnapshot.applicantsPerWeeklyTarget)} applicants`
      : 'more application data';
    const monthlyApplicantsText = admissionsSnapshot.applicantsPerMonthlyTarget
      ? `about ${formatNumber(admissionsSnapshot.applicantsPerMonthlyTarget)} applicants`
      : 'more application data';

    return `At the current-year conversion rate of ${formatPercent(admissionsSnapshot.yearSuccessRate)}, you need ${weeklyApplicantsText} to admit 5 students this week and ${monthlyApplicantsText} to admit 20 students this month.`;
  }, [admissionsSnapshot, allStudents.length]);

  return (
    <div className="clientsComp applicationsPage">
      <div className="appsHeader">
        <div>
          <p className="appsKicker">Admissions</p>
          <h4>Paid Fee, Not Joined</h4>
          <p className="appsSubtext">Students who have paid the admission fee but have not joined yet.</p>
        </div>
        <div className="appsHeaderStats">
          <div className="appsStatChip">
            <span>All</span>
            <strong>{totalCount}</strong>
          </div>
          <div className="appsStatChip">
            <span>Showing</span>
            <strong>{filteredCount}</strong>
          </div>
        </div>
      </div>

      <div className="appsInsightsCard">
        <div className="d-flex flex-wrap justify-content-between gap-3 align-items-start">
          <div>
            <p className="appsKicker">Admissions Snapshot</p>
            <h6 className="mb-1">Target: 5 per week, 20 per month</h6>
            <p className="appsSubtext">
              A quick read on how many students were admitted this week and month, plus the current-year conversion needed to stay on pace.
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <span className="cohortStatusBadge cohortStatusBadge--success">
              <FontAwesomeIcon icon={faUserCheck} className="me-1" />
              {loading ? '...' : `${formatNumber(admissionsSnapshot.thisWeekAdmissions)} admitted this week`}
            </span>
            <span className="cohortStatusBadge cohortStatusBadge--success">
              <FontAwesomeIcon icon={faUsers} className="me-1" />
              {loading ? '...' : `${formatNumber(admissionsSnapshot.thisMonthAdmissions)} admitted this month`}
            </span>
            <span className="cohortStatusBadge">
              <FontAwesomeIcon icon={faChartLine} className="me-1" />
              {loading ? '...' : `${formatPercent(admissionsSnapshot.yearSuccessRate)} year success rate`}
            </span>
            <span className="cohortStatusBadge cohortStatusBadge--pending">
              <FontAwesomeIcon icon={faBullseye} className="me-1" />
              {loading || admissionsSnapshot.applicantsPerWeeklyTarget === null
                ? 'Need more data'
                : `${formatNumber(admissionsSnapshot.applicantsPerWeeklyTarget)} applicants / 5 admits`}
            </span>
            <span className="cohortStatusBadge cohortStatusBadge--pending">
              <FontAwesomeIcon icon={faBullseye} className="me-1" />
              {loading || admissionsSnapshot.applicantsPerMonthlyTarget === null
                ? 'Need more data'
                : `${formatNumber(admissionsSnapshot.applicantsPerMonthlyTarget)} applicants / 20 admits`}
            </span>
          </div>
        </div>
        <div className="mt-3 appsSnapshotAdvice">
          <strong>Actionable read:</strong> {loading ? 'Loading admissions snapshot...' : admissionsGuidance}
          {!loading && admissionsSnapshot.weeklyGap > 0 && (
            <span> You are {formatNumber(admissionsSnapshot.weeklyGap)} admission{admissionsSnapshot.weeklyGap === 1 ? '' : 's'} short of the weekly target right now.</span>
          )}
          {!loading && admissionsSnapshot.monthlyGap > 0 && (
            <span> You are {formatNumber(admissionsSnapshot.monthlyGap)} admission{admissionsSnapshot.monthlyGap === 1 ? '' : 's'} short of the monthly target.</span>
          )}
        </div>
      </div>

      <div className="appsFiltersCard">
        <div className="appsSearch input-group">
          <span className="input-group-text"><FontAwesomeIcon icon={faSearch} /></span>
          <Form.Control
            type="text"
            placeholder="Search name, course, phone, cohort..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="appsFilterGroup">
          <Form.Select className="filterSelect" value={filterCourse} onChange={(e) => setFilterCourse(e.target.value)}>
            <option value="All">All Courses</option>
            {uniqueCourses.map((course) => (
              <option key={course} value={course}>{course}</option>
            ))}
          </Form.Select>
          <Form.Select className="filterSelect" value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)}>
            <option value="All">All Countries</option>
            {uniqueCountries.map((country) => (
              <option key={country} value={country}>{country}</option>
            ))}
          </Form.Select>
        </div>
      </div>

      <div className="appsTableCard">
        <div className="appsTableHeader">
          <h6>Awaiting Join</h6>
          <span>{filteredCount} results</span>
        </div>
        <div className="appsTableWrap">
          {loading ? (
            <div className="appsLoadingState">
              <Spinner animation="border" role="status" size="sm" />
              <p>Loading paid admission fee students...</p>
            </div>
          ) : (
            <Table hover responsive className="appsTable">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Paid Date</th>
                  <th>Course</th>
                  <th>Country</th>
                  <th>Phone</th>
                  <th>Cohort</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((student) => (
                  <tr key={student.id}>
                    <td>
                      <div className="appsStudentCell">
                        <div className="appDefProfPic">
                          <img src={student.profilePic ? student.profilePic : defaulProfPic} alt="profile" />
                        </div>
                        <div>
                          <p className="appsStudentName">{getStudentDisplayName(student)}</p>
                          <span className="appsStudentMeta">{student.course || 'Course not set'}</span>
                        </div>
                      </div>
                    </td>
                    <td>{formatDateTime(student.admissionFeePaidAt || student.appDate)}</td>
                    <td>{student.course || '—'}</td>
                    <td>{student.nationality || '—'}</td>
                    <td>
                      <MaskedPhone
                        phoneNumber={student.phoneNumber}
                        studentId={student.userId}
                        studentName={getStudentDisplayName(student)}
                      />
                    </td>
                    <td>{student.cohort || '—'}</td>
                    <td>
                      <button className="appsViewBtn" onClick={() => viewStudent(student.userId || student.id, getStudentDisplayName(student))}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredStudents.length === 0 && (
                  <tr>
                    <td colSpan="7" className="appsEmptyState">
                      No paid admission fee students are waiting to join right now.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}

export default ApplicationsAwaitingJoinComp;
